import React, { useState, useEffect, useMemo } from "react";
import {
  DndContext,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import axios from "../../api/api";
import { toast } from "react-toastify";
import SEOSection from "../../components/SEOSection/SEOSection";

const fieldConfigs = {
  title: {
    type: "title",
    label: "Title",
    placeholder: "Enter title (e.g., Kuta Selatan, Bali, Indonesia)",
    isTextarea: false,
  },
  description: {
    type: "description",
    label: "Description",
    placeholder: "Enter description",
    isTextarea: true,
  },
  check_list: {
    type: "check_list",
    label: "Check List (Highlights)",
    placeholder: "Enter items separated by new lines",
    isTextarea: true,
  },
  image: {
    type: "image",
    label: "Image",
    placeholder: "Enter image URL",
    isTextarea: false,
  },
  faq: {
    type: "faq",
    label: "FAQ",
    placeholder: "Enter question",
    isTextarea: false,
  },
  map: {
    type: "map",
    label: "Map",
    placeholder: "Enter map query (e.g., 'new york')",
    isTextarea: false,
  },
};

const initialContent = [
  {
    type: "title",
    data: "Kuta Selatan, Bali, Indonesia",
  },
  {
    type: "description",
    data: "We don’t just plan trips—we craft unforgettable experiences. With years of expertise, personalized service, and a deep passion for travel, we ensure every journey is smooth, safe, and inspiring. From to handpicked accommodations and seamless itineraries to local insights and 24/7 support, we take care of every detail so you can focus on creating memories. Whether it’s a relaxing getaway, an adventurous expedition, or a cultural escape, we go the extra mile to turn your travel dreams into reality.",
  },
  {
    type: "title",
    data: "Top Highlights",
  },
  {
    type: "description",
    data: "Our tours are designed to offer more than just a destination—they deliver unforgettable experiences with every step. From expertly curated itineraries and comfortable accommodations to guided local experiences and seamless travel logistics, we handle everything so you can relax.",
  },
  {
    type: "check_list",
    data: "Expertly Curated Itineraries\nLocal Guides & Authentic Experiences\nComfortable Transportation & Stays\nHassle-Free Travel Planning\nSmall Group or Private Tours\n24/7 Customer Support\nOne-Stop Travel Solutions\nAffordable & Transparent Pricing\nExclusive Deals & Discounts\nLocal Experiences & Cultural Immersion\nSecure Payment & Booking System\nTrusted Global Network",
  },
  {
    type: "image",
    // data: "/images/innerpage/destination/des-single3.jpg",
  },
  {
    type: "image",
    // data: "/images/innerpage/destination/des-single4.jpg",
  },
  {
    type: "title",
    data: "How to Choose?",
  },
  {
    type: "description",
    data: "Kuta Selatan, located in the southern tip of Bali, is a dream destination for travelers seeking the perfect balance of luxury, culture, and natural beauty. Home to iconic beaches like Pandawa and Dreamland, this area offers world-class resorts, dramatic cliffs, and vibrant local charm. Whether you're into surfing, sunsets, or serene temples like Uluwatu, Kuta Selatan delivers unforgettable experiences. It's less crowded than central Kuta, making it ideal for romantic getaways, family vacations, or wellness retreats. Easy access from Ngurah Rai International Airport and a variety of accommodation and activity options make it a smart and scenic choice for all kinds of travelers.",
  },
  {
    type: "faq",
    data: {
      question: "What services do you offer?",
      answer:
        "Travel with peace of mind knowing that 24/7 support, local expertise, and flexible booking options are always part of the journey. Whether you’re traveling solo, with family, or in a group, our tours combine comfort",
      // image: "/images/innerpage/gallery/faq-img1.jpg",
    },
  },
  {
    type: "faq",
    data: {
      question: "Can I customize my travel itinerary?",
      answer:
        "Travel with peace of mind knowing that 24/7 support, local expertise, and flexible booking options are always part of the journey. Whether you’re traveling solo, with family, or in a group, our tours combine comfort",
      // image: "/images/innerpage/gallery/faq-img1.jpg",
    },
  },
  {
    type: "faq",
    data: {
      question: "Are flights included in the tour packages?",
      answer:
        "Travel with peace of mind knowing that 24/7 support, local expertise, and flexible booking options are always part of the journey. Whether you’re traveling solo, with family, or in a group, our tours combine comfort",
      // image: "/images/innerpage/gallery/faq-img1.jpg",
    },
  },
  {
    type: "faq",
    data: {
      question: "Do I need travel insurance?",
      answer:
        "Travel with peace of mind knowing that 24/7 support, local expertise, and flexible booking options are always part of the journey. Whether you’re traveling solo, with family, or in a group, our tours combine comfort",
      // image: "/images/innerpage/gallery/faq-img1.jpg",
    },
  },
  {
    type: "map",
    data: "new york",
  },
];

const FieldInput = ({
  id,
  type,
  label,
  index,
  placeholder,
  isTextarea,
  removeField,
  dragListeners,
  depth,
  data,
}) => (
  <div
    className={`mb-4 p-4 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm ${
      depth > 0 ? "border-blue-500" : ""
    }`}
  >
    <div className="flex items-center justify-between mb-2">
      <div className="flex items-center">
        <div {...dragListeners} className="cursor-grab mr-2 text-gray-500">
          ≡
        </div>
        <span className="font-semibold text-lg text-gray-800 dark:text-gray-200">
          {label} {index !== null ? index + 1 : ""}
        </span>
      </div>
      <button
        type="button"
        onClick={removeField}
        className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
      >
        Remove
      </button>
    </div>
    {type === "faq" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Question
        </label>
        <input
          type="text"
          id={`question-${id}`}
          name={`question-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={placeholder}
          defaultValue={data?.question || ""}
          required
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          Answer
        </label>
        <textarea
          id={`answer-${id}`}
          name={`answer-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder="Enter answer"
          defaultValue={data?.answer || ""}
          required
        />
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mt-3 mb-1">
          FAQ Image (optional)
        </label>
        <input
          type="url"
          id={`url-faq-${id}`}
          name={`url-faq-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder="Enter image URL (optional)"
          defaultValue={
            data?.image && !data.image.startsWith("/uploads/") ? data.image : ""
          }
        />
        <div className="mt-3">
          <label
            htmlFor={`image-${id}`}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Or Upload Image (optional)
          </label>
          <input
            type="file"
            accept="image/*"
            id={`image-${id}`}
            name={`image-${id}`}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200"
          />
        </div>
        {data?.image && data.image.startsWith("/uploads/") && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Preview
            </label>
            <img
              src={`${process.env.REACT_APP_BACKEND_API_URL}${data.image}`}
              alt="Preview"
              style={{ maxWidth: "200px" }}
              onError={(e) => (e.target.src = "/fallback-image.png")}
            />
          </div>
        )}
      </>
    ) : type === "image" ? (
      <>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          URL (optional)
        </label>
        <input
          type="url"
          id={`url-${type}-${id}`}
          name={`url-${type}-${id}`}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
          placeholder={`${placeholder} (optional)`}
          defaultValue={data && !data.startsWith("/uploads/") ? data : ""}
        />
        <div className="mt-3">
          <label
            htmlFor={`image-${id}`}
            className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
          >
            Or Upload File (optional)
          </label>
          <input
            type="file"
            accept="image/*"
            id={`image-${id}`}
            name={`image-${id}`}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200"
          />
        </div>
        {data && data.startsWith("/uploads/") && (
          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Preview
            </label>
            <img
              src={`${process.env.REACT_APP_BACKEND_API_URL}${data}`}
              alt="Preview"
              style={{ maxWidth: "200px" }}
              onError={(e) => (e.target.src = "/fallback-image.png")}
            />
          </div>
        )}
      </>
    ) : isTextarea ? (
      <textarea
        id={`${type}-${id}`}
        name={`${type}-${id}`}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
        placeholder={placeholder}
        defaultValue={data || ""}
        required
      />
    ) : (
      <input
        type="text"
        id={`${type}-${id}`}
        name={`${type}-${id}`}
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
        placeholder={placeholder}
        defaultValue={data || ""}
        required
      />
    )}
  </div>
);

const TreeItem = ({
  item,
  index,
  parentId,
  depth,
  removeField,
  addNestedField,
  allFields,
  setFields,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const handleAddNested = (type) => {
    addNestedField(type, item.id);
  };

  return (
    <div ref={setNodeRef} style={style} className="relative">
      <FieldInput
        id={item.id}
        type={item.type}
        label={item.label}
        index={index}
        placeholder={item.placeholder}
        isTextarea={item.isTextarea}
        removeField={() => removeField(item.id)}
        dragListeners={{ ...listeners, ...attributes }}
        depth={depth}
        data={item.data}
      />
      {item.children?.length > 0 && (
        <div className="ml-6">
          {item.children.map((child, idx) => (
            <TreeItem
              key={child.id}
              item={child}
              index={idx}
              parentId={item.id}
              depth={depth + 1}
              removeField={removeField}
              addNestedField={addNestedField}
              allFields={allFields}
              setFields={setFields}
            />
          ))}
        </div>
      )}
      <div className="ml-6 mb-4">
        {Object.keys(fieldConfigs).map((fieldType) => (
          <button
            key={fieldType}
            onClick={() => handleAddNested(fieldType)}
            className="px-2 py-1 mr-2 bg-transparent border border-blue-500 text-blue-500 rounded-md hover:bg-blue-500 hover:text-white"
          >
            Add Nested {fieldConfigs[fieldType].label}
          </button>
        ))}
      </div>
    </div>
  );
};

function flattenTree(items) {
  const flat = [];
  const traverse = (nodes, depth = 0, parentId = null) => {
    nodes.forEach((node, index) => {
      flat.push({ ...node, depth, parentId, index });
      if (node.children) traverse(node.children, depth + 1, node.id);
    });
  };
  traverse(items);
  return flat;
}

function buildTree(flatItems) {
  const map = {};
  const tree = [];
  flatItems.forEach((item) => {
    map[item.id] = { ...item, children: [] };
  });
  flatItems.forEach((item) => {
    if (item.parentId) {
      map[item.parentId].children.push(map[item.id]);
    } else {
      tree.push(map[item.id]);
    }
  });
  return tree;
}

function getProjection(items, activeId, overId) {
  const flatItems = flattenTree(items);
  const activeItem = flatItems.find((item) => item.id === activeId);
  const overItem = flatItems.find((item) => item.id === overId);
  if (!activeItem || !overItem) return null;
  const maxDepth = Math.max(...flatItems.map((item) => item.depth));
  const minDepth = Math.min(...flatItems.map((item) => item.depth));
  const projectedDepth = Math.min(
    Math.max(activeItem.depth, overItem.depth),
    maxDepth,
  );
  return { parentId: overItem.parentId, depth: projectedDepth };
}

function getMaxDepth(items) {
  return Math.max(...flattenTree(items).map((item) => item.depth), 0);
}

function getMinDepth(items) {
  return Math.min(...flattenTree(items).map((item) => item.depth), 0);
}

function getParentId(items, id) {
  return flattenTree(items).find((item) => item.id === id)?.parentId;
}

function removeFromTree(items, id) {
  return items.reduce((acc, node) => {
    if (node.id !== id) {
      const newNode = { ...node };
      if (node.children) {
        newNode.children = removeFromTree(node.children, id);
      }
      acc.push(newNode);
    }
    return acc;
  }, []);
}

function getAllFields(items) {
  const flat = [];
  const traverse = (nodes) => {
    nodes.forEach((node) => {
      flat.push(node);
      if (node.children) traverse(node.children);
    });
  };
  traverse(items);
  return flat;
}

function hydrateContent(content, generateId) {
  if (!Array.isArray(content)) return [];
  return content
    .map((node) => {
      const config = fieldConfigs[node.type];
      if (!config) return null;
      const data =
        node.type === "faq"
          ? node.data || { question: "", answer: "", image: "" }
          : node.data || "";
      return {
        id: generateId(),
        ...config,
        data,
        children: node.children
          ? hydrateContent(node.children, generateId)
          : [],
      };
    })
    .filter(Boolean);
}

export default function AddDestination({
  isEdit = false,
  destinationId = null,
  onSuccess = () => {},
}) {
  const generateId = () =>
    crypto.randomUUID?.() || Math.random().toString(36).substr(2, 9);
  const [fields, setFields] = useState({
    allFields: hydrateContent(isEdit ? [] : initialContent, generateId),
  });
  const [activeId, setActiveId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [offsetLeft, setOffsetLeft] = useState(0);
  const [projected, setProjected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [name, setName] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [backgroundFile, setBackgroundFile] = useState(null);
  const [backgroundPreview, setBackgroundPreview] = useState("");
  const [photosFiles, setPhotosFiles] = useState([]);
  const [photosPreviews, setPhotosPreviews] = useState([]);
  const [existingPhotos, setExistingPhotos] = useState([]);
  const [visaRequired, setVisaRequired] = useState(false);
  const [pricePerPerson, setPricePerPerson] = useState(0);
  const [languages, setLanguages] = useState("");
  const [area, setArea] = useState("");
  const [seoData, setSeoData] = useState({
    metaTitle: "",
    metaDescription: "",
    metaKeywords: "",
    ogImage: "",
  });

  const handleSeoChange = (e) => {
    const { name, value } = e.target;
    setSeoData((prev) => ({ ...prev, [name]: value }));
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const addField = (type) => {
    const newField = {
      id: generateId(),
      ...fieldConfigs[type],
      data: type === "faq" ? { question: "", answer: "", image: "" } : "",
      children: [],
    };
    setFields((prev) => ({
      allFields: [...prev.allFields, newField],
    }));
  };

  const addNestedField = (type, parentId) => {
    const newField = {
      id: generateId(),
      ...fieldConfigs[type],
      data: type === "faq" ? { question: "", answer: "", image: "" } : "",
      children: [],
    };
    const newFields = JSON.parse(JSON.stringify(fields.allFields));
    const traverse = (nodes) => {
      nodes.forEach((node) => {
        if (node.id === parentId) {
          node.children = node.children
            ? [...node.children, newField]
            : [newField];
        } else if (node.children) {
          traverse(node.children);
        }
      });
    };
    traverse(newFields);
    setFields({ allFields: newFields });
  };

  const removeField = (id) => {
    setFields((prev) => ({
      allFields: removeFromTree(prev.allFields, id),
    }));
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
    setOverId(event.active.id);
    setProjected(
      getProjection(fields.allFields, event.active.id, event.active.id),
    );
  };

  const handleDragMove = (event) => {
    const { active, over } = event;
    setOverId(over?.id || null);
    if (over) {
      setProjected(getProjection(fields.allFields, active.id, over.id));
      setOffsetLeft(event.delta.x);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) {
      setActiveId(null);
      setOverId(null);
      setProjected(null);
      return;
    }

    const flatItems = flattenTree(fields.allFields);
    const activeItem = flatItems.find((item) => item.id === active.id);
    const overItem = flatItems.find((item) => item.id === over.id);

    if (!activeItem || !overItem) return;

    let newItems = [...fields.allFields];
    const activeParentId = getParentId(fields.allFields, active.id);
    const overParentId = getParentId(fields.allFields, over.id);
    const maxDepth = getMaxDepth(fields.allFields);
    const minDepth = getMinDepth(fields.allFields);
    const projectedDepth = projected ? projected.depth : activeItem.depth;

    if (activeParentId === overParentId) {
      const parentItems = activeParentId
        ? getAllFields(
            fields.allFields.find((item) => item.id === activeParentId)
              .children,
          )
        : fields.allFields;
      const activeIndex = parentItems.findIndex(
        (item) => item.id === active.id,
      );
      const overIndex = parentItems.findIndex((item) => item.id === over.id);
      const newParentItems = arrayMove(parentItems, activeIndex, overIndex);
      if (activeParentId) {
        newItems = fields.allFields.map((item) =>
          item.id === activeParentId
            ? { ...item, children: newParentItems }
            : item,
        );
      } else {
        newItems = newParentItems;
      }
    } else {
      const activeItemClone = JSON.parse(
        JSON.stringify(flatItems.find((item) => item.id === active.id)),
      );
      newItems = removeFromTree(fields.allFields, active.id);
      const targetParentId = projected ? projected.parentId : overParentId;
      if (!targetParentId) {
        newItems.splice(
          fields.allFields.findIndex((item) => item.id === over.id),
          0,
          { ...activeItemClone, depth: projectedDepth },
        );
      } else {
        newItems = newItems.map((item) => {
          if (item.id === targetParentId) {
            const insertIndex = item.children.findIndex(
              (child) => child.id === over.id,
            );
            return {
              ...item,
              children: [
                ...item.children.slice(0, insertIndex),
                { ...activeItemClone, depth: projectedDepth },
                ...item.children.slice(insertIndex),
              ],
            };
          }
          return item;
        });
      }
    }

    setFields({ allFields: newItems });
    setActiveId(null);
    setOverId(null);
    setProjected(null);
  };

  useEffect(() => {
    fetchCountries();
    if (isEdit && destinationId) {
      fetchDestinationById(destinationId);
    }
  }, [isEdit, destinationId]);

  const fetchCountries = async () => {
    try {
      const resp = await axios.get("/api/country");
      setCountries(resp.data.data || []);
    } catch (err) {
      toast.error("Failed to load countries");
      console.error("Fetch countries error:", err);
    }
  };

  const fetchStates = async (countryId) => {
    try {
      const resp = await axios.get(`/api/state/country/${countryId}`);
      setStates(resp.data.data || []);
    } catch (err) {
      toast.error("Failed to load states");
      console.error("Fetch states error:", err);
    }
  };

  useEffect(() => {
    if (selectedCountry) {
      fetchStates(selectedCountry);
    } else {
      setStates([]);
      setSelectedState("");
    }
  }, [selectedCountry]);

  const fetchDestinationById = async (id) => {
    setLoading(true);
    try {
      const resp = await axios.get(`/api/destination/${id}`);
      const dest = resp?.data?.data || resp?.data;
      if (dest) {
        const hydrated = hydrateContent(dest.content, generateId);
        setFields({ allFields: hydrated });
        const countryId = dest.state?.country?._id || dest.state?.country || "";
        const stateId = dest.state?._id || dest.state || "";

        setSelectedCountry(countryId);

        if (countryId) {
          try {
            const stateResp = await axios.get(
              `/api/state/country/${countryId}`,
            );
            setStates(stateResp.data.data || []);
          } catch (err) {
            console.error("Fetch states error:", err);
          }
        }

        setSelectedState(stateId);
        setName(dest.name || "");
        setShortDesc(dest.shortDescription || "");
        setBackgroundPreview(
          `${process.env.REACT_APP_BACKEND_API_URL}${dest.backgroundImage}`,
        );
        setExistingPhotos(dest.photos || []);
        setVisaRequired(dest.visaRequired);
        setPricePerPerson(dest.pricePerPerson || 0);
        setLanguages(dest.languages?.join(", ") || "");
        setArea(dest.area || "");
        setSeoData({
          metaTitle: dest.metaTitle || "",
          metaDescription: dest.metaDescription || "",
          metaKeywords: dest.metaKeywords || "",
          ogImage: dest.ogImage || "",
        });
      }
    } catch (err) {
      toast.error("Failed to load destination");
      console.error("Fetch destination error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackgroundChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBackgroundFile(file);
      setBackgroundPreview(URL.createObjectURL(file));
    }
  };

  const handlePhotosChange = (e) => {
    const files = Array.from(e.target.files);
    setPhotosFiles((prev) => [...prev, ...files]);
    setPhotosPreviews((prev) => [...prev, ...files.map(URL.createObjectURL)]);
  };

  const removeExistingPhoto = (photo) => {
    setExistingPhotos((prev) => prev.filter((p) => p !== photo));
  };

  const removeNewPhoto = (index) => {
    setPhotosFiles((prev) => prev.filter((_, i) => i !== index));
    setPhotosPreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCountry || !selectedState || !name) {
      toast.error(
        "Please fill all required fields: Country, State, and Location Name",
      );
      return;
    }
    setLoading(true);
    const formData = new FormData();
    const contentCopy = JSON.parse(JSON.stringify(fields.allFields));

    const traverseSetValues = (nodes) => {
      return nodes.map((node) => {
        const newNode = { type: node.type, id: node.id };
        if (["title", "description", "check_list", "map"].includes(node.type)) {
          const el = document.getElementById(`${node.type}-${node.id}`);
          const value = el?.value?.trim();
          if (!value && node.type !== "map") {
            toast.error(`${node.label} cannot be empty`);
            throw new Error(`${node.label} is required`);
          }
          newNode.data = value;
        } else if (node.type === "faq") {
          const qEl = document.getElementById(`question-${node.id}`);
          const aEl = document.getElementById(`answer-${node.id}`);
          const urlEl = document.getElementById(`url-faq-${node.id}`);
          const fileInput = document.getElementById(`image-${node.id}`);
          const question = qEl?.value?.trim() || "";
          const answer = aEl?.value?.trim() || "";
          let image = "";

          // Prioritize file upload over URL for FAQ image
          if (fileInput?.files?.[0]) {
            formData.append(`faq-image-${node.id}`, fileInput.files[0]);
          } else if (urlEl?.value?.trim()) {
            image = urlEl.value.trim();
          } else if (node.data?.image) {
            // Retain existing image if no new file or URL is provided
            image = node.data.image;
          }

          if (!question || !answer) {
            toast.error("All FAQs must have a question and answer");
            throw new Error("Invalid FAQ data");
          }
          newNode.data = { question, answer, image };
        } else if (node.type === "image") {
          let imgData = node.data || "";
          const urlEl = document.getElementById(`url-image-${node.id}`);
          const fileInput = document.getElementById(`image-${node.id}`);
          if (urlEl?.value?.trim()) {
            imgData = urlEl.value.trim();
          } else if (fileInput?.files?.[0]) {
            formData.append(`image-${node.id}`, fileInput.files[0]);
            imgData = "";
          }
          newNode.data = imgData;
        }
        if (node.children?.length) {
          newNode.children = traverseSetValues(node.children);
        }
        return newNode;
      });
    };

    let contentArray;
    try {
      contentArray = traverseSetValues(contentCopy);
    } catch (err) {
      setLoading(false);
      return;
    }

    // Append form fields to FormData
    formData.append("name", name || "");
    formData.append("shortDescription", shortDesc || "");
    formData.append("state", selectedState || "");
    formData.append("visaRequired", visaRequired.toString());
    formData.append("pricePerPerson", pricePerPerson.toString());
    formData.append("languages", languages || "");
    formData.append("area", area || "");
    formData.append("metaTitle", seoData.metaTitle);
    formData.append("metaDescription", seoData.metaDescription);
    formData.append("metaKeywords", seoData.metaKeywords);
    formData.append("ogImage", seoData.ogImage);
    formData.append("content", JSON.stringify(contentArray));
    formData.append("existingPhotos", JSON.stringify(existingPhotos));

    if (backgroundFile) {
      formData.append("galleryImage-background", backgroundFile);
    }
    photosFiles.forEach((file, index) => {
      formData.append(`galleryImage-${index}`, file);
    });

    try {
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      };
      let resp;
      if (isEdit) {
        resp = await axios.put(
          `/api/destination/${destinationId}`,
          formData,
          config,
        );
        toast.success("Destination updated successfully!");
      } else {
        resp = await axios.post(
          "/api/destination/add-destination",
          formData,
          config,
        );
        toast.success("Destination added successfully!");
      }
      onSuccess();
    } catch (err) {
      toast.error(
        `Failed to ${isEdit ? "update" : "add"} destination: ${err.message}`,
      );
      console.error("Submission error:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status,
      });
    } finally {
      setLoading(false);
    }
  };

  const activeItem = useMemo(
    () => flattenTree(fields.allFields).find((item) => item.id === activeId),
    [activeId, fields.allFields],
  );

  return (
    <div className="flex flex-1 bg-gray-100 dark:bg-gray-900">
      <div className="page-wrapper relative ml-auto w-[calc(100%-260px)] px-6 pt-24 duration-300">
        <div className="xl:w-full min-h-[calc(100vh-152px)] relative pb-14">
          <div className="grid md:grid-cols-12 lg:grid-cols-12 xl:grid-cols-12 gap-6 mb-6">
            <div className="sm:col-span-12 md:col-span-12 lg:col-span-8 xl:col-span-6 xl:col-start-4">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg w-full relative mb-6 shadow-md">
                <div className="border-b border-gray-200 dark:border-gray-700 py-4 px-6 dark:text-gray-300">
                  <h4 className="font-semibold text-xl flex-1 self-center mb-2 md:mb-0">
                    {isEdit ? "Edit Destination" : "Add Destination"}
                  </h4>
                </div>
                <form onSubmit={handleSubmit} className="flex-auto p-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Country
                    </label>
                    <select
                      value={selectedCountry}
                      onChange={(e) => setSelectedCountry(e.target.value)}
                      name="country"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
                      required
                    >
                      <option value="">Select Country</option>
                      {countries.map((country) => (
                        <option key={country._id} value={country._id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      State
                    </label>
                    <select
                      value={selectedState}
                      onChange={(e) => setSelectedState(e.target.value)}
                      name="state"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
                      disabled={!selectedCountry}
                      required
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state._id} value={state._id}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Location Name
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      name="name"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
                      placeholder="Enter location name (e.g., Kuta Selatan)"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Short Description
                    </label>
                    <textarea
                      value={shortDesc}
                      onChange={(e) => setShortDesc(e.target.value)}
                      name="shortDescription"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
                      placeholder="Enter short description"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Background Image (One)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleBackgroundChange}
                      name="galleryImage-background"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200"
                    />
                    {backgroundPreview && (
                      <img
                        src={backgroundPreview}
                        alt="Background Preview"
                        className="mt-2"
                        style={{ maxWidth: "200px" }}
                      />
                    )}
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Additional Photos (Multiple)
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handlePhotosChange}
                      name="galleryImage"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200"
                    />
                    <div className="flex flex-wrap mt-2">
                      {existingPhotos.map((photo, index) => (
                        <div key={`existing-${index}`} className="m-1 relative">
                          <img
                            src={`${process.env.REACT_APP_BACKEND_API_URL}${photo}`}
                            alt="Existing Photo"
                            width={100}
                          />
                          <button
                            type="button"
                            onClick={() => removeExistingPhoto(photo)}
                            className="absolute top-0 right-0 bg-red-500 text-white p-1"
                          >
                            X
                          </button>
                        </div>
                      ))}
                      {photosPreviews.map((preview, index) => (
                        <div key={`new-${index}`} className="m-1 relative">
                          <img src={preview} alt="New Photo" width={100} />
                          <button
                            type="button"
                            onClick={() => removeNewPhoto(index)}
                            className="absolute top-0 right-0 bg-red-500 text-white p-1"
                          >
                            X
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Visa Required
                    </label>
                    <select
                      value={visaRequired}
                      onChange={(e) =>
                        setVisaRequired(e.target.value === "true")
                      }
                      name="visaRequired"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
                    >
                      <option value={false}>No</option>
                      <option value={true}>Yes</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Price Per Person ($)
                    </label>
                    <input
                      type="number"
                      value={pricePerPerson}
                      onChange={(e) => setPricePerPerson(e.target.value)}
                      name="pricePerPerson"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
                      placeholder="Enter price"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Languages (comma separated)
                    </label>
                    <input
                      type="text"
                      value={languages}
                      onChange={(e) => setLanguages(e.target.value)}
                      name="languages"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
                      placeholder="English, Indonesian"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Area (e.g., 90.000 Km2)
                    </label>
                    <input
                      type="text"
                      value={area}
                      onChange={(e) => setArea(e.target.value)}
                      name="area"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 bg-transparent dark:text-gray-200 placeholder-gray-400"
                      placeholder="Enter area"
                    />
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCorners}
                    onDragStart={handleDragStart}
                    onDragMove={handleDragMove}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={fields.allFields.map((item) => item.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {fields.allFields.map((item, index) => (
                        <TreeItem
                          key={item.id}
                          item={item}
                          index={index}
                          parentId={null}
                          depth={0}
                          removeField={removeField}
                          addNestedField={addNestedField}
                          allFields={fields.allFields}
                          setFields={setFields}
                        />
                      ))}
                    </SortableContext>
                    <DragOverlay>
                      {activeId && activeItem && (
                        <FieldInput
                          id={activeItem.id}
                          type={activeItem.type}
                          label={activeItem.label}
                          index={activeItem.index}
                          placeholder={activeItem.placeholder}
                          isTextarea={activeItem.isTextarea}
                          removeField={() => {}}
                          dragListeners={{}}
                          depth={projected?.depth || activeItem.depth}
                          data={activeItem.data}
                        />
                      )}
                    </DragOverlay>
                  </DndContext>

                  <SEOSection formData={seoData} onChange={handleSeoChange} />

                  <div className="flex justify-end mt-6">
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                      disabled={loading}
                    >
                      {isEdit ? "Update" : "Submit"}
                    </button>
                  </div>
                </form>
                <div className="p-6 pt-0">
                  <div className="mt-8">
                    <h5 className="font-semibold text-base text-gray-700 dark:text-gray-300 mb-3">
                      Add Fields
                    </h5>
                    <div className="flex flex-wrap gap-3">
                      {Object.keys(fieldConfigs).map((fieldType) => (
                        <button
                          key={fieldType}
                          onClick={() => addField(fieldType)}
                          className="px-4 py-2 bg-transparent border border-blue-500 text-blue-500 rounded-md hover:bg-blue-500 hover:text-white"
                        >
                          Add {fieldConfigs[fieldType].label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
